import { type ApiResponse } from '@types';
import { type DispositionRecord } from '../../../types/dispositions/dispositionEnums.ts';
import { microServicesRoutes } from '@constants';
import { syncApi } from '@lib/apiClients.ts';
import { handleError } from '@utils/handleAPIError.ts';
import {
  type CreateOutreachCallDispositionDto,
  type OutreachDisposition,
  type OutreachDispositionsResponseDto,
  type OutreachMapping,
  type OutreachMappingRecords, type UpdateOutreachCallDispositionDto
} from '../../../types/integrations/outreach/outreachMappingsTypes.ts';
import { type PatchMappingDto } from '../../../types/integrations/genericTypes.ts';

interface OutreachMappingsApi {
    getOutreachMappingsByOrgId: (organizationId: string) => Promise<ApiResponse<OutreachMappingRecords>>
    getOutreachCustomDispositions: (
        organizationId: string,
        nonce: string,
    ) => Promise<ApiResponse<OutreachDisposition[]>>,
    patchOutreachMapping: (
        id: number | string,
        patchOutreachMappingDto: PatchMappingDto
    ) => Promise<ApiResponse<OutreachDisposition>>,
  createOutreachDisposition: (
      organizationId: string,
      nonce: string,
      createOutreachMappingDto: CreateOutreachCallDispositionDto)=> Promise<ApiResponse<OutreachDisposition>>,
  updateOutreachDisposition: (
      organizationId: string,
      nonce: string,
      createOutreachMappingDto: UpdateOutreachCallDispositionDto)=> Promise<ApiResponse<OutreachDisposition>>
  deleteOutreachDisposition: (
      organizationId: string,
      nonce: string,
      callDispositionId: number)=> Promise<ApiResponse<void>>
}


export function UseOutreachMappingsApi (): OutreachMappingsApi {

  const rootGetRoute = microServicesRoutes.sync.get.outreach.mappings;
  const rootPostRoute = microServicesRoutes.sync.post.outreach.mappings;
  const rootPutRoute = microServicesRoutes.sync.put.outreach.mappings;
  const rootDeleteRoute = microServicesRoutes.sync.delete.outreach.mappings;

  function transformStringMappingsToRecord (outreachMapping: OutreachMapping): OutreachMappingRecords {
    return {
      ...outreachMapping,
      mapping: JSON.parse(String(outreachMapping.mapping)) as DispositionRecord,
    };
  }

  async function getOutreachMappingsByOrgId
  (organizationId: string): Promise<ApiResponse<OutreachMappingRecords>> {
    let mappings = {
      data: undefined,
      success: false
    } as unknown as ApiResponse<OutreachMappingRecords>;

    try {
      const url = `${rootGetRoute.getMappingsByOrgId}/${organizationId}`;
      const mappingsResponse: ApiResponse<OutreachMapping> = await syncApi.get(url);


      if (mappingsResponse.data !== undefined) {
        const { data } = mappingsResponse;
        mappings = {
          data: transformStringMappingsToRecord(data),
          success: true
        };
      }

      return mappings;
    } catch (e: unknown) {
      return handleError(
        'Error while linking outreach mapping.',
        e,
        getOutreachMappingsByOrgId.name
      );
    }
  }

  async function getOutreachCustomDispositions (
    organizationId: string,
    nonce: string,
  ): Promise<ApiResponse<OutreachDisposition[]>> {
    try {
      const url = `${rootGetRoute.getCustomDispositions}/${organizationId}/${nonce}`;

      const dispositions = await syncApi.get<OutreachDispositionsResponseDto>(url);
      
      return {
        success: true,
        data: dispositions.data?.data
      };
    } catch (e: unknown) {
      return handleError(
        'Error while linking outreach custom disposition.',
        e,
        getOutreachCustomDispositions.name
      );
    }
  }

  async function createOutreachDisposition (
    organizationId: string,
    nonce: string,
    createOutreachMappingDto: CreateOutreachCallDispositionDto): Promise<ApiResponse<OutreachDisposition>> {
    try {
      const url = `${rootPostRoute.createOutreachDisposition}/${organizationId}/${nonce}`;
      const createResponse = 
          await syncApi.post<OutreachDisposition>(url, createOutreachMappingDto);
      return {
        success: true,
        data: createResponse.data
      };
    } catch (e) {
      return handleError(
        'Error while creating outreach custom disposition.',
        e,
        createOutreachDisposition.name
      );
    }
  }
  
  async function updateOutreachDisposition (
    organizationId: string,
    nonce: string,
    updateOutreachMappingDto: UpdateOutreachCallDispositionDto
  ):  Promise<ApiResponse<OutreachDisposition>> {
    try {
      const url = `${rootPutRoute.updateOutreachDisposition}/${organizationId}/${nonce}`;
      const updateResponse =
          await syncApi.post<OutreachDisposition>(url, updateOutreachMappingDto);
      return {
        success: true,
        data: updateResponse.data
      };
    } catch (e) {
      return handleError(
        'Error while updating outreach custom disposition.',
        e,
        updateOutreachDisposition.name
      );
    }
  }

  async function deleteOutreachDisposition ( 
    organizationId: string,
    nonce: string,
    callDispositionId: number): Promise<ApiResponse<void>> {
    try {
      const url = `${rootDeleteRoute.deleteCustomDisposition}/${organizationId}/${nonce}/${callDispositionId}`;
      return await syncApi.delete(url);
    } catch (e) {
      return handleError(
        'Error while deleting outreach custom disposition.',
        e,
        deleteOutreachDisposition.name
      );
    }
  }

  async function patchOutreachMapping (
    id: number | string,
    patchOutreachMappingDto: PatchMappingDto
  ): Promise<ApiResponse<OutreachDisposition>> {
    try {
      const url = `${rootPostRoute.patchOutreachMapping}/${id}`;
      const patchResponse = await syncApi.post<OutreachDisposition>(url, patchOutreachMappingDto);
      return {
        success: true,
        data: patchResponse.data
      };
    } catch (e: unknown) {
      return handleError(
        'Error while patching outreach disposition',
        e,
        patchOutreachMapping.name
      );
    }
  }

  return {
    getOutreachMappingsByOrgId,
    getOutreachCustomDispositions,
    createOutreachDisposition,
    updateOutreachDisposition,
    deleteOutreachDisposition,
    patchOutreachMapping,

  };
}